/**
 * FeaturePageGate Component
 * 
 * Full-page wrapper that blocks access when a feature is not in the plan.
 * Shows an animated upgrade panel with use-case cards.
 * During active trial: shows a banner warning that the feature is trial-only.
 * 
 * Usage in App.jsx routes:
 *   <FeaturePageGate featureKey="sales.enabled" featureName="Sales Intelligence">
 *     <SalesIntelligencePage />
 *   </FeaturePageGate>
 * 
 * During active trial: all features are unlocked, but trial-only features
 * show a countdown banner at the top.
 * SuperAdmins always bypass.
 * 
 * Non-admins (agents/viewers): ver tela neutra "não disponível" em vez do
 * painel de upgrade comercial — eles não tomam decisões de compra.
 */
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAuth } from '../contexts/AuthContext';
import FeatureUpgradePanel from './FeatureUpgradePanel';
import TrialFeatureBanner from './TrialFeatureBanner';

function FeatureUnavailableScreen({ featureName }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-4 h-48 w-48">
          <DotLottieReact src="/assets/Forgot Password.lottie" loop autoplay />
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-1">
          {featureName || 'Esta funcionalidade'} não está disponível
        </h2>
        <p className="text-sm text-foreground-muted">
          Esta funcionalidade não está incluída no plano atual.
          Fale com o administrador da sua conta para mais informações.
        </p>
      </div>
    </div>
  );
}

export default function FeaturePageGate({ children, featureKey, featureName }) {
  const { hasFeature, isSuperAdmin, isTrial, isTrialOnlyFeature, isAdmin } = useAuth();

  // SuperAdmins always bypass
  if (isSuperAdmin()) return children;

  // During trial, everything is unlocked — but show banner for trial-only features
  if (isTrial) {
    return (
      <>
        <TrialFeatureBanner featureKey={featureKey} />
        {children}
      </>
    );
  }

  // Check if feature is available in current plan
  if (hasFeature(featureKey)) return children;

  // Feature not available:
  // Admins (level >= 50) see the upgrade/upsell panel.
  // Agents/viewers see a neutral "not available" screen — they can't buy.
  if (isAdmin()) {
    return <FeatureUpgradePanel featureKey={featureKey} featureName={featureName} variant="full" />;
  }

  return <FeatureUnavailableScreen featureName={featureName} />;
}
